import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Fragment, useContext, useEffect, useState } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { ApplicationContext } from '~/shared/context/context';
import type { JobFamily } from '~/shared/data-contract/job-family';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { ShakeAnimation } from '~/ui/animations/shake/ShakeAnimation';
import { useShakeOnClickOutside } from '~/ui/animations/shake/use-shake-on-click-outside';
import { Button } from '~/ui/core/button/Button';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Textarea } from '~/ui/core/textarea/Textarea';
import { Tile, TileContent, TileStickyHeader } from '~/ui/core/tile/Tile';
import { SidepanelHeader } from '~/ui/domain/Sidepanel/components/SidepanelHeader';

import { useTileNavigation, useUrlTileEscapeClose } from '../../url-tiles';
import { useJobFamilyDetails } from '../api/use-job-family-details';
import { usePatchJobFamily } from '../api/use-patch-job-family';
import { Action, CTAWrapper } from '../components/Action';
import { Backdrop, ContentContainer, Wrapper } from '../components/Common';
import { ErrorContent } from '../components/ErrorContent';
import type { SaveOptions } from '../components/SaveWithAutoTranslationButton';
import { SaveWithAutoTranslationButton } from '../components/SaveWithAutoTranslationButton';
import { SidepanelTitle } from '../components/SidepanelTitle';
import { DescriptionSectionSkeleton, TitleSkeleton } from '../components/SkeletonLoaders';
import { useDiscardConfirmation } from '../hooks/use-discard-confirmation';
import { useSetupLanguage } from '../hooks/use-language';
import { JSA_PRIMARY_DETAIL_TILE_SLOT_KEY } from '../url-tile-payloads';
import { JobFamilyOptionsMenu } from './OptionsMenu';
import type { JobFamilyForm } from './types';

const StyledTextarea = styled(Textarea)(({ theme }) => ({
    fontSize: theme.typography.body.S.regular.fontSize,
    lineHeight: '21px',
    marginBottom: theme.spacing.xxl,
    marginTop: theme.spacing.xxl,
    minHeight: '400px',
}));

const DescriptionSection: FunctionComponent<{
    readonly description?: string;
    readonly isEditing: boolean;
    readonly register: UseFormRegisterReturn<`translations.${number}.description`>;
}> = ({ description, isEditing, register }) => {
    if (isEditing) {
        return <StyledTextarea {...register} aria-label="description" />;
    }

    return <p>{description}</p>;
};

const JobFormForm: FunctionComponent<{
    readonly activeLanguage: LanguageCodeForJSA;
    readonly activeLanguageIndex: number;
    readonly family: JobFamily;
    readonly isEditing: boolean;
    readonly jobArchitectureId: string;
    readonly onTileClose: () => void;
    readonly setIsEditing: (isEditing: boolean) => void;
}> = ({ activeLanguage, activeLanguageIndex, family, isEditing, jobArchitectureId, onTileClose, setIsEditing }) => {
    const formMethods = useForm<JobFamilyForm>({
        defaultValues: {
            translations: family.translations,
        },
    });
    const { handleSubmit, register, reset, setValue } = formMethods;

    const { translate } = useContext(ApplicationContext);
    const activeTranslation = family.translations[activeLanguageIndex];
    const { mutateAsync: updateJobFamily } = usePatchJobFamily();

    const { handleCancel, handleCancelDiscard, handleClose, handleConfirmDiscard, isDiscardDialogOpen } =
        useDiscardConfirmation({
            formMethods,
            isEditing,
            onClose: onTileClose,
            setIsEditing,
        });

    useUrlTileEscapeClose(handleClose);

    useEffect(() => {
        if (isEditing === false) {
            reset();
            setValue('translations', family.translations);
        }
    }, [reset, isEditing, setValue, family.translations]);

    const handleSave = async (jobFamily: JobFamilyForm, { withAutoTranslation }: SaveOptions): Promise<void> => {
        try {
            await updateJobFamily({
                jobFamily: {
                    ...family,
                    architectureId: jobArchitectureId,
                    translations: jobFamily.translations.filter((_, index) => index === activeLanguageIndex),
                },
                languageCode: activeLanguage,
                withAutoTranslation,
            });
        } finally {
            setIsEditing(false);
            reset();
        }
    };

    return (
        <Fragment>
            <ConfirmationDialog
                cancelButtonLabel={translate('common.cancel')}
                confirmButtonLabel={translate('common.confirm')}
                confirmButtonVariant="default"
                description={translate('dialogs.discard.content')}
                open={isDiscardDialogOpen}
                title={translate('dialogs.discard.title')}
                onCancel={handleCancelDiscard}
                onConfirm={handleConfirmDiscard}
            />
            <SidepanelTitle
                key={`title-${activeLanguageIndex}`}
                isEditing={isEditing}
                register={register(`translations.${activeLanguageIndex}.title`)}
                title={activeTranslation?.title ?? ''}
            />
            <Action style={{ justifyContent: 'flex-end' }}>
                {!isEditing ? (
                    <CTAWrapper>
                        <Button
                            disabled={!family.actions.canEdit}
                            variant="tertiary"
                            onClick={() => setIsEditing(true)}
                        >
                            {translate('common.edit')}
                        </Button>
                    </CTAWrapper>
                ) : (
                    <CTAWrapper>
                        <Button variant="tertiary" onClick={handleCancel}>
                            {translate('common.cancel')}
                        </Button>
                        <SaveWithAutoTranslationButton
                            onSave={(options) => handleSubmit((form) => handleSave(form, options))()}
                        />
                    </CTAWrapper>
                )}
            </Action>
            <DescriptionSection
                key={`description-${activeLanguageIndex}`}
                description={activeTranslation?.description}
                isEditing={isEditing}
                register={register(`translations.${activeLanguageIndex}.description`)}
            />
        </Fragment>
    );
};

export const JobFamilyDetails: FunctionComponent<{
    readonly architectureId: string;
    readonly familyId: string;
    readonly mode: 'edit' | 'view';
    readonly onActiveLanguageChange?: (language: LanguageCodeForJSA) => void;
    readonly onClose: () => void;
}> = ({ architectureId, familyId, mode, onActiveLanguageChange, onClose }) => {
    const { data: family, error, isError, isLoading } = useJobFamilyDetails(architectureId, familyId);

    const { activeLanguage, activeLanguageIndex, availableLanguages, setActiveLanguage } = useSetupLanguage(
        undefined,
        family,
    );

    const [isEditing, setIsEditing] = useState(mode === 'edit');
    const { isShaking, shake } = useShakeOnClickOutside();

    const handleLanguageChange = (language: LanguageCodeForJSA): void => {
        setActiveLanguage(language);
        onActiveLanguageChange?.(language);
    };

    const tileNavigation = useTileNavigation(`${JSA_PRIMARY_DETAIL_TILE_SLOT_KEY}.${architectureId}.${familyId}`);
    const { translate } = useContext(ApplicationContext);

    const header = (
        <TileStickyHeader>
            <SidepanelHeader
                actions={family === undefined ? undefined : <JobFamilyOptionsMenu family={family} onClose={onClose} />}
                activeLanguage={
                    activeLanguage === undefined
                        ? undefined
                        : translate(`common.languages.${activeLanguage}` as TranslationKey)
                }
                isGenerating={family?.generationInfo?.inProgress ?? false}
                isLanguagePickerDisabled={isEditing}
                languages={availableLanguages}
                setActiveLanguage={handleLanguageChange}
                onClose={onClose}
                onNext={tileNavigation?.onNext}
                onPrevious={tileNavigation?.onPrevious}
            />
        </TileStickyHeader>
    );

    if (isLoading) {
        return (
            <Tile data-testid="job-family-details-loading">
                <ScrollArea>
                    <TileContent>
                        <ContentContainer>
                            <Wrapper>
                                {header}
                                <div style={{ marginBottom: '-1.8rem', marginTop: '3.3rem' }}>
                                    <TitleSkeleton />
                                </div>
                                <Action>
                                    <div style={{ height: '2.2rem' }} />
                                </Action>
                                <DescriptionSectionSkeleton />
                            </Wrapper>
                        </ContentContainer>
                    </TileContent>
                </ScrollArea>
            </Tile>
        );
    }

    if (isError || is.undefined(family)) {
        return (
            <Tile>
                <ScrollArea>
                    <TileContent>
                        <ContentContainer>
                            <ErrorContent error={error} onClose={onClose} />
                        </ContentContainer>
                    </TileContent>
                </ScrollArea>
            </Tile>
        );
    }

    return (
        <Fragment>
            {isEditing && <Backdrop onClick={shake} />}
            <ShakeAnimation isShaking={isShaking}>
                <Tile data-testid="job-family-details">
                    <ScrollArea>
                        <TileContent>
                            <ContentContainer>
                                <Wrapper>
                                    {header}
                                    <JobFormForm
                                        key={`job-form-form-${activeLanguageIndex}`}
                                        activeLanguage={activeLanguage}
                                        activeLanguageIndex={activeLanguageIndex}
                                        family={family}
                                        isEditing={isEditing}
                                        jobArchitectureId={architectureId}
                                        setIsEditing={setIsEditing}
                                        onTileClose={onClose}
                                    />
                                </Wrapper>
                            </ContentContainer>
                        </TileContent>
                    </ScrollArea>
                </Tile>
            </ShakeAnimation>
        </Fragment>
    );
};
